<template>
  <section>
    <titlebar :title-stack="['Avaliação do curso']" />
    <b-loading :active="loading" />

    <template v-if="curso">
      <header class="avaliacao-header box">
        <div>
          <p class="avaliacao-header__legenda">Avaliação final</p>
          <h1 class="title is-3 mb-2">{{ curso.nome }}</h1>
          <p class="has-text-grey">
            Configure a nota mínima e mantenha as perguntas apresentadas ao aluno.
          </p>
        </div>
        <router-link
          class="button"
          :to="{ name: 'ead-curso-conteudo', params: { id: curso.id } }"
        >
          <b-icon icon="arrow-left" size="is-small" />
          <span>Voltar ao conteúdo</span>
        </router-link>
      </header>

      <form class="box" @submit.prevent="salvarConfiguracao">
        <div class="configuracao-titulo">
          <div>
            <h2 class="title is-5 mb-1">Dados da avaliação</h2>
            <p class="is-size-7 has-text-grey">
              A avaliação passa a fazer parte do curso quando possui ao menos uma pergunta.
            </p>
          </div>
          <b-tag :type="perguntas.length ? 'is-success' : 'is-light'">
            {{ perguntas.length ? 'Configurada' : 'Sem perguntas' }}
          </b-tag>
        </div>
        <div class="configuracao-campos">
          <b-field label="Nome da avaliação">
            <b-input
              v-model="avaliacao.nome"
              required
              maxlength="160"
              :disabled="!podeAlterar"
            />
          </b-field>
          <b-field label="Nota mínima (%)">
            <b-numberinput
              v-model="avaliacao.notaMinima"
              min="0"
              max="100"
              :disabled="!podeAlterar"
            />
          </b-field>
        </div>
        <div v-if="podeAlterar" class="configuracao-acoes">
          <b-button
            native-type="submit"
            type="is-primary"
            icon-left="content-save"
            :loading="salvandoConfiguracao"
          >
            Salvar dados
          </b-button>
        </div>
      </form>

      <section class="box">
        <div class="perguntas-header">
          <div>
            <h2 class="title is-5 mb-1">Perguntas</h2>
            <p class="is-size-7 has-text-grey">
              {{ perguntas.length }}
              {{ perguntas.length === 1 ? 'pergunta cadastrada' : 'perguntas cadastradas' }}
            </p>
          </div>
          <b-button
            v-if="podeAlterar"
            type="is-primary"
            icon-left="plus"
            @click="novaPergunta"
          >
            Nova pergunta
          </b-button>
        </div>

        <b-table
          :data="perguntas"
          :mobile-cards="true"
          striped
          hoverable
        >
          <b-table-column field="ordem" label="Ordem" width="90" centered v-slot="p">
            <span class="ordem-badge">{{ p.row.ordem }}</span>
          </b-table-column>
          <b-table-column field="enunciado" label="Pergunta" v-slot="p">
            <p class="has-text-weight-semibold">{{ p.row.enunciado }}</p>
            <div class="alternativas-resumo">
              <span
                v-for="alternativa in p.row.alternativas"
                :key="alternativa.id"
                :class="{ 'alternativa-correta': alternativa.correta }"
              >
                <b-icon
                  :icon="alternativa.correta ? 'check-circle' : 'circle-outline'"
                  size="is-small"
                />
                {{ alternativa.descricao }}
              </span>
            </div>
          </b-table-column>
          <b-table-column v-slot="p" label="Ações" width="190" centered>
            <div v-if="podeAlterar" class="buttons is-centered mb-0">
              <b-button
                size="is-small"
                icon-left="pencil"
                @click="editarPergunta(p.row)"
              >
                Editar
              </b-button>
              <b-button
                size="is-small"
                type="is-danger is-light"
                icon-left="delete-outline"
                :loading="removendoId === p.row.id"
                @click="confirmarRemocao(p.row)"
              >
                Remover
              </b-button>
            </div>
            <span v-else class="has-text-grey-light">—</span>
          </b-table-column>
          <template #empty>
            <div v-if="!loading" class="estado-vazio">
              <b-icon icon="clipboard-text-outline" size="is-medium" />
              <p class="has-text-weight-semibold">Nenhuma pergunta cadastrada</p>
              <p class="is-size-7">
                Inclua a primeira pergunta para habilitar a avaliação final do curso.
              </p>
            </div>
          </template>
        </b-table>
      </section>
    </template>

    <b-modal :active.sync="modalPergunta" has-modal-card :width="760">
      <form class="modal-card avaliacao-modal" @submit.prevent="salvarPergunta">
        <header class="modal-card-head">
          <div class="modal-titulo">
            <b-icon icon="help-circle-outline" type="is-primary" />
            <div>
              <p class="modal-card-title">
                {{ pergunta.id ? 'Editar pergunta' : 'Nova pergunta' }}
              </p>
              <p class="is-size-7 has-text-grey">
                Cadastre as alternativas e marque exatamente uma resposta correta.
              </p>
            </div>
          </div>
        </header>
        <section class="modal-card-body">
          <b-field label="Enunciado">
            <b-input
              v-model="pergunta.enunciado"
              type="textarea"
              rows="3"
              required
              maxlength="2000"
            />
          </b-field>
          <b-field label="Ordem de exibição">
            <b-numberinput v-model="pergunta.ordem" min="1" />
          </b-field>

          <div class="alternativas-bloco">
            <div class="alternativas-header">
              <p class="has-text-weight-semibold">Alternativas</p>
              <b-button size="is-small" icon-left="plus" @click.prevent="adicionarAlternativa">
                Adicionar alternativa
              </b-button>
            </div>
            <div
              v-for="(alternativa, index) in pergunta.alternativas"
              :key="alternativa.id || `nova-${index}`"
              class="alternativa-row"
            >
              <b-radio v-model="correta" :native-value="index" />
              <span class="alternativa-numero">{{ index + 1 }}</span>
              <b-input
                v-model="alternativa.descricao"
                expanded
                required
                maxlength="1000"
                :placeholder="`Alternativa ${index + 1}`"
              />
              <b-button
                size="is-small"
                type="is-text"
                icon-left="delete-outline"
                title="Remover alternativa"
                :disabled="pergunta.alternativas.length <= 2"
                @click.prevent="removerAlternativa(index)"
              />
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button @click="modalPergunta = false">Cancelar</b-button>
          <b-button
            native-type="submit"
            type="is-primary"
            icon-left="content-save"
            :loading="salvandoPergunta"
          >
            Salvar pergunta
          </b-button>
        </footer>
      </form>
    </b-modal>
  </section>
</template>

<script>
import http from '@/http'
import titlebar from '@/components/titlebar.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'EadAvaliacaoConteudo',
  components: { titlebar },
  data: () => ({
    curso: null,
    avaliacao: {
      id: 0,
      nome: 'Avaliação final',
      notaMinima: 70,
    },
    perguntas: [],
    pergunta: { alternativas: [] },
    correta: 0,
    loading: false,
    modalPergunta: false,
    salvandoConfiguracao: false,
    salvandoPergunta: false,
    removendoId: null,
  }),
  computed: {
    ...mapGetters(['isInRole']),
    podeAlterar() {
      return this.isInRole('rh-diretoria')
    },
  },
  mounted() {
    this.carregar()
  },
  methods: {
    alternativa() {
      return { id: 0, descricao: '', correta: false, ordem: 1, ativo: true }
    },
    carregar() {
      this.loading = true
      return http
        .get(`/api/ead-conteudo/cursos/${this.$route.params.id}/avaliacao`)
        .then((r) => {
          this.curso = r.data.curso
          if (r.data.avaliacao) {
            this.avaliacao = {
              id: r.data.avaliacao.id,
              nome: r.data.avaliacao.nome,
              notaMinima: r.data.avaliacao.notaMinima,
            }
            this.perguntas = r.data.avaliacao.perguntas || []
          } else {
            this.avaliacao = {
              id: 0,
              nome: 'Avaliação final',
              notaMinima: 70,
            }
            this.perguntas = []
          }
        })
        .finally(() => (this.loading = false))
    },
    dadosAvaliacao() {
      return {
        nome: this.avaliacao.nome,
        notaMinima: this.avaliacao.notaMinima,
        perguntas: [],
      }
    },
    salvarConfiguracao() {
      this.salvandoConfiguracao = true
      return http
        .post(
          `/api/ead-conteudo/cursos/${this.$route.params.id}/avaliacao`,
          this.dadosAvaliacao()
        )
        .then(() => {
          this.$buefy.toast.open({
            message: 'Dados da avaliação salvos.',
            type: 'is-success',
          })
          return this.carregar()
        })
        .finally(() => (this.salvandoConfiguracao = false))
    },
    novaPergunta() {
      this.correta = 0
      this.pergunta = {
        id: 0,
        enunciado: '',
        tipo: 1,
        obrigatoria: true,
        ordem: this.perguntas.length + 1,
        ativo: true,
        alternativas: [this.alternativa(), this.alternativa()],
      }
      this.modalPergunta = true
    },
    editarPergunta(pergunta) {
      const alternativas = (pergunta.alternativas || []).map((alternativa) => ({
        ...alternativa,
        ativo: true,
      }))
      const correta = alternativas.findIndex((alternativa) => alternativa.correta)
      this.correta = correta >= 0 ? correta : 0
      this.pergunta = {
        ...pergunta,
        tipo: 1,
        obrigatoria: true,
        ativo: true,
        alternativas,
      }
      this.modalPergunta = true
    },
    adicionarAlternativa() {
      this.pergunta.alternativas.push(this.alternativa())
    },
    removerAlternativa(index) {
      if (this.pergunta.alternativas.length <= 2) return
      this.pergunta.alternativas.splice(index, 1)
      if (this.correta === index) this.correta = 0
      else if (this.correta > index) this.correta--
    },
    normalizarAlternativas() {
      this.pergunta.alternativas.forEach((alternativa, index) => {
        alternativa.ordem = index + 1
        alternativa.correta = index === this.correta
        alternativa.ativo = true
      })
    },
    garantirAvaliacao() {
      if (this.avaliacao.id) return Promise.resolve()
      return http.post(
        `/api/ead-conteudo/cursos/${this.$route.params.id}/avaliacao`,
        this.dadosAvaliacao()
      )
    },
    salvarPergunta() {
      this.salvandoPergunta = true
      this.normalizarAlternativas()
      const base = `/api/ead-conteudo/cursos/${this.$route.params.id}/avaliacao/perguntas`
      this.garantirAvaliacao()
        .then(() =>
          this.pergunta.id
            ? http.put(`${base}/${this.pergunta.id}`, this.pergunta)
            : http.post(base, this.pergunta)
        )
        .then(() => {
          this.modalPergunta = false
          this.$buefy.toast.open({
            message: 'Pergunta salva.',
            type: 'is-success',
          })
          return this.carregar()
        })
        .finally(() => (this.salvandoPergunta = false))
    },
    confirmarRemocao(pergunta) {
      this.$buefy.dialog.confirm({
        title: 'Remover pergunta',
        message:
          'A pergunta deixará de aparecer nas próximas avaliações. O histórico das tentativas será preservado.',
        confirmText: 'Remover',
        cancelText: 'Cancelar',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.removerPergunta(pergunta),
      })
    },
    removerPergunta(pergunta) {
      this.removendoId = pergunta.id
      http
        .delete(
          `/api/ead-conteudo/cursos/${this.$route.params.id}/avaliacao/perguntas/${pergunta.id}`
        )
        .then(() => {
          this.$buefy.toast.open({
            message: 'Pergunta removida.',
            type: 'is-success',
          })
          return this.carregar()
        })
        .finally(() => (this.removendoId = null))
    },
  },
}
</script>

<style scoped>
.avaliacao-header,
.configuracao-titulo,
.perguntas-header,
.modal-titulo,
.alternativas-header,
.alternativa-row {
  align-items: center;
  display: flex;
}

.avaliacao-header,
.configuracao-titulo,
.perguntas-header,
.alternativas-header {
  justify-content: space-between;
  gap: 1rem;
}

.avaliacao-header {
  border-left: 4px solid #00b89c;
}

.avaliacao-header__legenda {
  color: #7a7a7a;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 0.35rem;
  text-transform: uppercase;
}

.configuracao-titulo,
.perguntas-header {
  margin-bottom: 1.25rem;
}

.configuracao-campos {
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 2fr) minmax(180px, 1fr);
}

.configuracao-acoes {
  display: flex;
  justify-content: flex-end;
}

.ordem-badge,
.alternativa-numero {
  align-items: center;
  background: #f0f0f0;
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 auto;
  font-weight: 700;
  height: 1.75rem;
  justify-content: center;
  width: 1.75rem;
}

.alternativas-resumo {
  color: #7a7a7a;
  display: flex;
  flex-direction: column;
  font-size: 0.8rem;
  gap: 0.2rem;
  margin-top: 0.5rem;
}

.alternativas-resumo span {
  align-items: center;
  display: inline-flex;
  gap: 0.3rem;
}

.alternativa-correta {
  color: #257953;
  font-weight: 600;
}

.estado-vazio {
  color: #7a7a7a;
  padding: 2rem;
  text-align: center;
}

.avaliacao-modal {
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
}

.modal-titulo,
.alternativa-row {
  gap: 0.65rem;
}

.modal-card-foot {
  justify-content: flex-end;
}

.alternativas-bloco {
  background: #fafafa;
  border: 1px solid #ededed;
  border-radius: 7px;
  padding: 1rem;
}

.alternativas-header {
  margin-bottom: 0.85rem;
}

.alternativa-row {
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 6px;
  padding: 0.6rem;
}

.alternativa-row + .alternativa-row {
  margin-top: 0.5rem;
}

@media screen and (max-width: 768px) {
  .avaliacao-header,
  .configuracao-titulo,
  .perguntas-header,
  .alternativas-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .configuracao-campos {
    grid-template-columns: 1fr;
  }

  .avaliacao-header .button,
  .perguntas-header .button {
    width: 100%;
  }

  .alternativa-row {
    align-items: flex-start;
  }
}
</style>
